/**
 * session.ts
 *
 * The one way a session ends in this app.
 *
 * Every path that used to clear auth and navigate on its own — the axios 401/403
 * interceptors, the v5 fetch wrapper, the sidebar's Sign out button, and now the
 * idle watcher — calls `endSession(reason)` instead. It clears state and records
 * WHY, but deliberately does not navigate. `SessionWatcher` observes
 * `authStore.endReason` and performs the redirect from an effect.
 *
 * Why the indirection matters
 * ---------------------------
 * Four components install `beforeunload` guards for unsaved work
 * (hooks/useUnsavedChangesGuard.ts, v5/contexts/NoteFormContext.tsx,
 * UnifiedAppointmentModal.tsx, RecurringDetailModal.tsx). Navigating the document
 * while one is live fires the browser's native "Leave site?" prompt and can park
 * the user on a page that is still rendering PHI.
 *
 * Clearing `token` makes RequireAuth render <Navigate>, which unmounts Layout and
 * its whole subtree. React runs every unmount cleanup before any new passive
 * effect, so all four listeners — and useUnsavedChangesGuard's `pushState`
 * monkey-patch — are gone by the time the redirect effect runs. Ordering is
 * therefore guaranteed rather than hoped for; `setTimeout(…, 0)` is not
 * equivalent, because React 18 schedules its work on a MessageChannel macrotask
 * and "usually wins the race" is how an intermittent prompt ships.
 *
 * Using react-router `navigate()` instead would be strictly worse:
 * useUnsavedChangesGuard intercepts `pushState` and raises a blocking
 * window.confirm with PHI still behind it.
 *
 * The redirect is a full document load on purpose. It's the only real guarantee
 * that nothing PHI-bearing survives in memory — React Query cache, closures,
 * detached DOM, Sentry breadcrumbs. `queryClient.clear()` below is belt-and-braces
 * for any future soft path, not the mechanism.
 */

import { useAuthStore } from '@/store/authStore';
import { queryClient } from '@/lib/queryClient';
import { clearIdleRecord } from '@/lib/idleSession';
import { buildCorporateLoginUrl } from '@/lib/corpAuth';
import type { SessionEndReason } from '@/types/auth.types';

/** Per-tab latch. */
let ending = false;

const REASON_KEY = 'ftp.logoutReason';

/**
 * Remember why the session ended, for the login screen to read after the
 * document reloads.
 *
 * sessionStorage rather than localStorage so the message is scoped to this tab —
 * it must not surface in other tabs or in future sessions in the same browser.
 *
 * The route guard's soft <Navigate> mounts LoginPage the moment the token is
 * cleared, before SessionWatcher's redirect has left the document, and that
 * <Navigate> carries no query string. This copy is how LoginPage knows the
 * session was ended on purpose, so its own bounce to corp SSO forces the
 * account list too. It also feeds the banner on the break-glass form.
 */
function rememberReason(reason: SessionEndReason): void {
  try {
    window.sessionStorage.setItem(REASON_KEY, reason);
  } catch {
    // Private mode / storage disabled — the query param still carries it.
  }
}

/** Read the reason and clear it, so a refresh doesn't show the message again. */
export function consumeLogoutReason(): SessionEndReason | null {
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(REASON_KEY);
    window.sessionStorage.removeItem(REASON_KEY);
  } catch {
    stored = null;
  }

  const fromQuery = new URLSearchParams(window.location.search).get('reason');
  const value = stored ?? fromQuery;

  return value === 'idle' || value === 'expired' || value === 'restricted' || value === 'user'
    ? value
    : null;
}

/**
 * Tell the server to revoke this session, in a way that survives the redirect.
 *
 * Deliberately a raw `fetch` with `keepalive: true` rather than the axios client:
 *
 *  - `keepalive` is what lets the request outlive the document. SessionWatcher
 *    navigates as soon as `endReason` is set, which aborts ordinary in-flight
 *    requests — so a normal POST here would frequently never reach the server and
 *    the token would stay valid, silently defeating the revocation.
 *  - The token is passed in explicitly because the store is cleared moments later;
 *    apiClient's request interceptor reads the token from the store and would
 *    attach nothing.
 *  - Going direct also keeps `lib/session` off `services/api`, which imports
 *    apiClient, which imports this module — a cycle worth not having.
 *
 * Fire-and-forget by design: the user is signing out either way, and /auth/logout
 * is exempt from the 401 interceptor so a failure can't re-enter endSession().
 *
 * `reason` lets the server's activity record tell a sign-out for inactivity
 * from a manual one: an idle timeout the app noticed first reaches this
 * endpoint exactly like a click on Sign out. Only `idle` means inactivity;
 * every other ending is reported as `user`.
 */
function revokeServerSide(token: string, reason: SessionEndReason): void {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v5';
  try {
    void fetch(`${base}/auth/logout`, {
      method: 'POST',
      keepalive: true,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason: reason === 'idle' ? 'idle' : 'user' }),
    }).catch(() => undefined);
  } catch {
    // Older browsers without keepalive support, or a blocked request. The token
    // then expires on its own schedule, as it did before this endpoint existed.
  }
}

/**
 * End the current session.
 *
 * @param reason        why it ended — drives the login-screen message
 * @param notifyServer  POST /auth/logout to revoke the token server-side. True
 *                      for a deliberate sign-out and for an idle timeout the
 *                      client detected first; false when the server already told
 *                      us the session is gone (a 401), since the call would just
 *                      401 again.
 */
export function endSession(reason: SessionEndReason, notifyServer = false): void {
  // Single-flight. main.tsx sets refetchOnWindowFocus, so focusing a dead tab
  // fires a burst of parallel requests; without this latch each resulting 401
  // would clear state and queue its own redirect.
  if (ending) return;
  ending = true;

  const store = useAuthStore.getState();
  const token = store.token;

  if (notifyServer && token) {
    revokeServerSide(token, reason);
  }

  rememberReason(reason);
  clearIdleRecord();

  // Clearing the token is what unmounts the PHI-bearing tree (see the file
  // docblock); setEndReason is the signal SessionWatcher redirects on.
  store.logout();
  store.setEndReason(reason);

  try {
    queryClient.clear();
  } catch {
    // Never let cache teardown block the sign-out.
  }
}

/**
 * Where to send the browser once React has unmounted.
 *
 * The corporate login, for every reason: the corp auth app is the only way to
 * sign in, and this app's own /login form is deprecated. `prompt=select_account`
 * forces the corp account list so a single-account user isn't signed straight
 * back in — the same URL `redirectToCorpLogout()` builds.
 *
 * This app's /login, with the reason, only when no corp app is configured
 * (local dev), since the form is then the only sign-in there is.
 *
 * Note what this does NOT do: the corp app keeps its own rolling session, so
 * signing back in is one click and no password. Closing that gap needs a change
 * in corp-prospera-auth — see docs/idle-timeout.md.
 */
export function buildRedirectUrl(reason: SessionEndReason): string {
  return (
    buildCorporateLoginUrl({ forceAccountSelection: true }) ??
    `/login?reason=${encodeURIComponent(reason)}`
  );
}

/** Test-only: drop the single-flight latch between cases. */
export function resetSessionEndLatchForTests(): void {
  ending = false;
}
